import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { getApiErrorMessage } from '../../utils/apiError';

export type GroupBy = 'day' | 'group' | 'student' | 'student_lessons';
export type PercentGroupBy = 'percent_day';

export interface AbsencesFilters {
  dateFrom?: string;
  dateTo?: string;
  groupIds?: number[];
  level?: string;
  top?: number; // applies to student
  studentId?: number; // for student_lessons
}

export interface DayPoint { date: string; absences: number; }
export interface GroupPoint { groupId: number; groupName: string | null; level: string; absences: number; }
export interface StudentPoint { studentId: number; firstName: string; lastName: string; absences: number; }
export interface StudentLessonPoint { lessonId: number; date: string; groupId: number; groupName: string | null; level: string; }
export interface PercentDayPoint { date: string; totalStudents: number; absentCount: number; percent: number; }
export interface GroupTimeseriesPoint { date: string; groupId: number; groupName: string | null; absences: number; }
export interface StudentTimeseriesPoint { date: string; studentId: number; firstName: string; lastName: string; absences: number; }

export interface AnalyticsState {
  loading: boolean;
  error: string | null;
  filters: AbsencesFilters;
  byDay: DayPoint[];
  byGroup: GroupPoint[];
  topStudents: StudentPoint[];
  studentLessons: StudentLessonPoint[];
  percentByDay: PercentDayPoint[];
  groupTimeseries: GroupTimeseriesPoint[];
  studentTimeseries: StudentTimeseriesPoint[];
}

const initialState: AnalyticsState = {
  loading: false,
  error: null,
  filters: {},
  byDay: [],
  byGroup: [],
  topStudents: [],
  studentLessons: [],
  percentByDay: [],
  groupTimeseries: [],
  studentTimeseries: [],
};

const API_BASE_URL = import.meta.env.VITE_API_URL as string;

const getAuthHeaders = (getState: () => unknown) => {
  const state = getState() as { auth?: { token?: string | null } };
  const token = state?.auth?.token;
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const fetchGroupTimeseries = createAsyncThunk(
  'analytics/fetchGroupTimeseries',
  async (filters: AbsencesFilters, { rejectWithValue, getState }) => {
    try {
      const params = new URLSearchParams();
      params.set('groupBy', 'group_timeseries');
      if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.set('dateTo', filters.dateTo);
      if (filters.groupIds && filters.groupIds.length > 0) params.set('groupIds', filters.groupIds.join(','));
      if (filters.level) params.set('level', filters.level);
      const res = await axios.get(`${API_BASE_URL}/analytics/absences?${params.toString()}`, {
        headers: getAuthHeaders(getState),
      });
      return res.data.data as GroupTimeseriesPoint[];
    } catch (e) {
      return rejectWithValue(getApiErrorMessage(e, 'Failed to load group timeseries'));
    }
  }
);

export const fetchStudentTimeseries = createAsyncThunk(
  'analytics/fetchStudentTimeseries',
  async (filters: AbsencesFilters, { rejectWithValue, getState }) => {
    try {
      const params = new URLSearchParams();
      params.set('groupBy', 'student_timeseries');
      if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.set('dateTo', filters.dateTo);
      if (filters.groupIds && filters.groupIds.length > 0) params.set('groupIds', filters.groupIds.join(','));
      if (filters.level) params.set('level', filters.level);
      params.set('top', String(filters.top ?? 5));
      const res = await axios.get(`${API_BASE_URL}/analytics/absences?${params.toString()}`, {
        headers: getAuthHeaders(getState),
      });
      return res.data.data as StudentTimeseriesPoint[];
    } catch (e) {
      return rejectWithValue(getApiErrorMessage(e, 'Failed to load student timeseries'));
    }
  }
);

const buildParams = (filters: AbsencesFilters, groupBy: GroupBy) => {
  const params = new URLSearchParams();
  params.set('groupBy', groupBy);
  if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
  if (filters.dateTo) params.set('dateTo', filters.dateTo);
  if (filters.groupIds && filters.groupIds.length > 0) params.set('groupIds', filters.groupIds.join(','));
  if (filters.level) params.set('level', filters.level);
  if (groupBy === 'student' && filters.top) params.set('top', String(filters.top));
  if (groupBy === 'student_lessons' && filters.studentId) params.set('studentId', String(filters.studentId));
  return params;
};

const buildPercentParams = (filters: AbsencesFilters, groupBy: PercentGroupBy) => {
  const params = new URLSearchParams();
  params.set('groupBy', groupBy);
  if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
  if (filters.dateTo) params.set('dateTo', filters.dateTo);
  if (filters.groupIds && filters.groupIds.length > 0) params.set('groupIds', filters.groupIds.join(','));
  if (filters.level) params.set('level', filters.level);
  return params;
};

export const fetchAbsencesByDay = createAsyncThunk(
  'analytics/fetchByDay',
  async (filters: AbsencesFilters, { rejectWithValue, getState }) => {
    try {
      const params = buildParams(filters, 'day');
      const res = await axios.get(`${API_BASE_URL}/analytics/absences?${params.toString()}`, {
        headers: getAuthHeaders(getState),
      });
      return res.data.data as DayPoint[];
    } catch (e) {
      return rejectWithValue(getApiErrorMessage(e, 'Failed to load absences by day'));
    }
  }
);

export const fetchAbsencesByGroup = createAsyncThunk(
  'analytics/fetchByGroup',
  async (filters: AbsencesFilters, { rejectWithValue, getState }) => {
    try {
      const params = buildParams(filters, 'group');
      const res = await axios.get(`${API_BASE_URL}/analytics/absences?${params.toString()}`, {
        headers: getAuthHeaders(getState),
      });
      return res.data.data as GroupPoint[];
    } catch (e) {
      return rejectWithValue(getApiErrorMessage(e, 'Failed to load absences by group'));
    }
  }
);

export const fetchTopStudents = createAsyncThunk(
  'analytics/fetchTopStudents',
  async (filters: AbsencesFilters, { rejectWithValue, getState }) => {
    try {
      const params = buildParams({ ...filters, top: filters.top ?? 5 }, 'student');
      const res = await axios.get(`${API_BASE_URL}/analytics/absences?${params.toString()}`, {
        headers: getAuthHeaders(getState),
      });
      return res.data.data as StudentPoint[];
    } catch (e) {
      return rejectWithValue(getApiErrorMessage(e, 'Failed to load top students'));
    }
  }
);

export const fetchStudentLessons = createAsyncThunk(
  'analytics/fetchStudentLessons',
  async (filters: AbsencesFilters, { rejectWithValue, getState }) => {
    try {
      const params = buildParams(filters, 'student_lessons');
      const res = await axios.get(`${API_BASE_URL}/analytics/absences?${params.toString()}`, {
        headers: getAuthHeaders(getState),
      });
      return res.data.data as StudentLessonPoint[];
    } catch (e) {
      return rejectWithValue(getApiErrorMessage(e, 'Failed to load student lessons'));
    }
  }
);

export const fetchPercentByDay = createAsyncThunk(
  'analytics/fetchPercentByDay',
  async (filters: AbsencesFilters, { rejectWithValue, getState }) => {
    try {
      const params = buildPercentParams(filters, 'percent_day');
      const res = await axios.get(`${API_BASE_URL}/analytics/absences?${params.toString()}`, {
        headers: getAuthHeaders(getState),
      });
      return res.data.data as PercentDayPoint[];
    } catch (e) {
      return rejectWithValue(getApiErrorMessage(e, 'Failed to load percent by day'));
    }
  }
);

const analyticsSlice = createSlice({
  name: 'analytics',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<AbsencesFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearStudentLessons: (state) => {
      state.studentLessons = [];
      delete state.filters.studentId;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAbsencesByDay.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchAbsencesByDay.fulfilled, (state, action) => { state.loading = false; state.byDay = action.payload; })
      .addCase(fetchAbsencesByDay.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; })

      .addCase(fetchAbsencesByGroup.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchAbsencesByGroup.fulfilled, (state, action) => { state.loading = false; state.byGroup = action.payload; })
      .addCase(fetchAbsencesByGroup.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; })

      .addCase(fetchTopStudents.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchTopStudents.fulfilled, (state, action) => { state.loading = false; state.topStudents = action.payload; })
      .addCase(fetchTopStudents.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; })

      .addCase(fetchStudentLessons.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchStudentLessons.fulfilled, (state, action) => { state.loading = false; state.studentLessons = action.payload; })
      .addCase(fetchStudentLessons.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; })

      .addCase(fetchPercentByDay.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchPercentByDay.fulfilled, (state, action) => { state.loading = false; state.percentByDay = action.payload; })
      .addCase(fetchPercentByDay.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; });

      builder
        .addCase(fetchGroupTimeseries.pending, (state) => { state.loading = true; state.error = null; })
        .addCase(fetchGroupTimeseries.fulfilled, (state, action) => { state.loading = false; state.groupTimeseries = action.payload; })
        .addCase(fetchGroupTimeseries.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; });

      builder
        .addCase(fetchStudentTimeseries.pending, (state) => { state.loading = true; state.error = null; })
        .addCase(fetchStudentTimeseries.fulfilled, (state, action) => { state.loading = false; state.studentTimeseries = action.payload; })
        .addCase(fetchStudentTimeseries.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; });
  },
});

export const { setFilters, clearStudentLessons } = analyticsSlice.actions;
export default analyticsSlice.reducer;
